import { markRaw } from 'vue';
import PinOverlayIcon from '../icons/collection/PinOverlayIcon.vue';
import TerrainMapIcon from '../icons/basemaps/TerrainMapIcon.vue';
import RoadsMapIcon from '../icons/basemaps/RoadsMapIcon.vue';
import HybridMapIcon from '../icons/basemaps/HybridMapIcon.vue';
import LandownerLayersIcon from '../icons/maps/LandownerLayersIcon.vue';
import SubscriptionLayersIcon from '../icons/maps/SubscriptionLayersIcon.vue';
import HuntzoneIcon from '../icons/maps/HuntzoneIcon.vue';
import ResourceLayersIcon from '../icons/maps/ResourceLayersIcon.vue';
import CustomMapIcon from '../icons/maps/CustomMapIcon.vue';

import { collectionConstants } from './useCollection';
import { CollectionItem } from 'ihunter-common';

// Regions constant to provide a fixed order
const Regions = Object.freeze(['bc', 'ab', 'sk', 'mb', 'on', 'qc', 'nb', 'nl', 'ns', 'yt']);
const Locales = Object.freeze(['en', 'fr']);

const { PinSize } = collectionConstants();

const AuthState = Object.freeze({
    SignIn: 'sign_in',
    CreateAccount: 'create_account',
    ValidatingSignInLink: 'validating_sign_in_link',
    SignInWithLink: 'sign_in_with_link',
    SignInWithLinkError: 'sign_in_with_link_error',
    SignInWithPassword: 'sign_in_with_password',
    SignOut: 'sign_out',
    SignInLinkSent: 'sign_in_link_sent',
    AcceptInviteLinkResent: 'accept_invite_link_resent',
    RequestResetPassword: 'request_reset_password',
    ResetPasswordLinkSent: 'reset_password_link_sent',
});

const AuthType = Object.freeze({
    AppleProvider: 'appleProvider',
    FacebookProvider: 'facebookProvider',
    GoogleProvider: 'googleProvider',
    InviteLink: 'inviteLink',
    Password: 'password',
    ResetPassword: 'resetPassword',
    SignInLink: 'signInLink',
});

const ComponentState = Object.freeze({
    Default: 'default',
    Secondary: 'secondary',
    Loading: 'loading',
    Success: 'success',
    Error: 'error',
    Timeout: 'timeout',
    Critical: 'critical',
    Action: 'action',
    Selected: 'selected',
    Disabled: 'disabled',
});

const ComponentVariant = Object.freeze({
    Default: 'default',
    Box: 'box',
    Inline: 'inline',
    Tucked: 'tucked',
    Tab: 'tab',
    Sortlist: 'sortlist',
    Thumbnail: 'thumbnail',
    ThumbnailTight: 'thumbnail-tight',
    Small: 'small',
    Promotion: 'promotion',
    Update: 'update',
});

const ListSortOptions = Object.freeze({
    All: 'all',
    Active: 'active',
    Name: 'name',
    Date: 'date',
    Type: 'type',
});

const ListSortOrders = Object.freeze({
    All: 'all',
    NewestDate: 'newest',
    OldestDate: 'oldest',
    AscName: 'az',
    DescName: 'za',
    Active: 'active',
    NotActive: 'not_active',
    AscType: 'az_type',
    DescType: 'za_type',
});

const SeasonSummaries = Object.freeze([
    {
        value: 'big_game',
        title: 'regulations.big_game',
        iconComponent: markRaw(PinOverlayIcon),
        iconProps: { overlayName: CollectionItem.Waypoint.PinOverlay.Moose, size: PinSize.Map },
    },
    {
        value: 'predators',
        title: 'regulations.predators',
        iconComponent: markRaw(PinOverlayIcon),
        iconProps: { overlayName: CollectionItem.Waypoint.PinOverlay.Bear, size: PinSize.Map },
    },
    {
        value: 'small_game',
        title: 'regulations.small_game',
        iconComponent: markRaw(PinOverlayIcon),
        iconProps: { overlayName: CollectionItem.Waypoint.PinOverlay.Bird, size: PinSize.Map },
    },
]);

const MapEngine = Object.freeze({
    Mapbox: 'mapbox',
    Leaflet: 'leaflet',
});

const MapProvider = Object.freeze({
    Mapbox: 'mapbox',
    Google: 'google',
    Apple: 'apple',
    Custom: 'custom',
});

const MapStyle = Object.freeze({
    Hybrid: 'hybrid',
    Road: 'roads',
    Terrain: 'terrain',
});

const BasemapOptions = Object.freeze(
    [
        {
            label: 'basemap.providers.mapbox',
            engine: MapEngine.Mapbox,
            provider: MapProvider.Mapbox,
            default: true,
        },
        // {
        //     label: 'basemap.providers.google',
        //     engine: MapEngine.Mapbox,
        //     provider: MapProvider.Google,
        // },
        {
            label: 'basemap.providers.mapbox',
            engine: MapEngine.Leaflet,
            provider: MapProvider.Mapbox,
        },
        {
            label: 'basemap.providers.google',
            engine: MapEngine.Leaflet,
            provider: MapProvider.Google,
        },
        {
            label: 'basemap.providers.apple',
            engine: MapEngine.Leaflet,
            provider: MapProvider.Apple,
        },
    ].map((x) => {
        return { ...x, ...{ value: `${x.label}-${x.engine}-${x.provider}` } };
    }),
);
const BasemapDefault = BasemapOptions.find((x) => x.default === true);

const BasemapTypeOptions = Object.freeze([
    {
        value: MapStyle.Hybrid,
        label: 'common.imagery.hybrid',
        iconComponent: markRaw(HybridMapIcon),
        default: true,
    },
    {
        value: MapStyle.Terrain,
        label: 'common.imagery.terrain',
        iconComponent: markRaw(TerrainMapIcon),
    },
    {
        value: MapStyle.Road,
        label: 'common.imagery.road',
        iconComponent: markRaw(RoadsMapIcon),
    },
]);
const BasemapTypeDefault = BasemapTypeOptions.find((x) => x.default === true);

const GlyphType = Object.freeze({
    Alert: 'alert',
    Altitude: 'altitude',
    Arrow: 'arrow',
    Back: 'back',
    Chevron: 'chevron',
    Close: 'close',
    Collapse: 'collapse',
    CollapseTiny: 'collapse-tiny',
    Coords: 'coords',
    CoordsSelected: 'coords-selected',
    Expand: 'expand',
    ExpandTiny: 'expand-tiny',
    External: 'external',
    Info: 'info',
    Lightbox: 'lightbox',
    Location: 'location',
    LocationJumpTo: 'location-jump-to',
    LocationPill: 'location-pill',
    Menu: 'menu',
    Plus: 'plus',
    Pro: 'pro',
    Success: 'success',
    Layers: 'layers',
});

const ResourceName = Object.freeze({
    Resources: 'resources',
    Subscriptions: 'subscriptions',
});

const LayerGroup = Object.freeze({
    BuiltInLayer: 'layers',
    WMU: 'wmu',
    LEH: 'leh',
    County: 'county',
    Purchasable: 'purchasable_layers', // special case to search for counties by term/location
    Subscription: 'subscription',
    CustomLayer: 'custom',
});

const LayerGroupOptions = Object.freeze(
    Object.fromEntries(
        [
            {
                id: LayerGroup.BuiltInLayer,
                iconComponent: markRaw(ResourceLayersIcon),
            },
            {
                id: LayerGroup.WMU,
                iconComponent: markRaw(HuntzoneIcon),
            },
            {
                id: LayerGroup.LEH,
                iconComponent: markRaw(HuntzoneIcon),
            },
            {
                id: LayerGroup.County,
                iconComponent: markRaw(LandownerLayersIcon),
            },
            {
                id: LayerGroup.Purchasable,
                iconComponent: markRaw(LandownerLayersIcon),
            },
            {
                id: LayerGroup.Subscription,
                iconComponent: markRaw(SubscriptionLayersIcon),
            },
            {
                id: LayerGroup.CustomLayer,
                iconComponent: markRaw(CustomMapIcon),
            },
        ].map((options) => [options.id, options]),
    ),
);

const PurchaseStatus = Object.freeze({
    PurchasedLatest: 'purchased_latest',
    PurchasedUpradable: 'purchased_upgradable',
    NotPurchased: 'not_purchased',
    Unavailable: 'unavailable',
});

const ChatType = Object.freeze({
    ContactChat: 'contact',
    GroupChat: 'group',
});

const ResultType = Object.freeze({
    Boundary: 'boundary',
    BoundaryHidden: 'boundary_hidden',
    Collection: 'collection',
    CollectionFiltered: 'collection_filtered', // hidden by the collection filter
    CollectionHidden: 'collection_hidden', // My collection is hidden in Map View
    Coordinates: 'coordinates',
    LandownerMap: 'landownerMap',
    Layer: 'layer',
    Web: 'web',
});

const SubscriptionTier = Object.freeze({
    Free: 'free',
    Core: 'core',
    Pro: 'pro',
});

const SubscriptionSku = Object.freeze({
    Core: '_core',
    Pro: '_publicland',
    AllMaps: 'all_maps',
});

const ResourceType = Object.freeze({
    ExternalLink: 'externalLink',
    Pdf: 'pdf',
    Text: 'text',
});

const CustomLayerType = Object.freeze({
    Default: 0,
    Basemap: 1,
    Overlay: 2,
});

const TileServiceType = Object.freeze({
    None: 0,
    XYZ: 1,
    WMS: 2,
    TMS: 3,
});
export {
    AuthState,
    AuthType,
    BasemapDefault,
    BasemapOptions,
    BasemapTypeDefault,
    BasemapTypeOptions,
    ChatType,
    CustomLayerType,
    ComponentState,
    ComponentVariant,
    GlyphType,
    PurchaseStatus,
    LayerGroup,
    LayerGroupOptions,
    Locales,
    ListSortOptions,
    ListSortOrders,
    MapEngine,
    MapProvider,
    MapStyle,
    Regions,
    ResourceName,
    ResourceType,
    ResultType,
    SeasonSummaries,
    SubscriptionSku,
    SubscriptionTier,
    TileServiceType,
};
